import { v4 as uuidv4 } from "uuid";
import {
  Logger,
  shallowCopy,
  Graph,
  isUndefined,
  Matrix2D,
  Matrix2DFields,
  identityMatrix2DFields,
} from "@noli/core";
import { CacheType, CloudCacheType, PostCloudProjectResponse } from "@/types/cache";
import { loginStore } from "@/modules/login/store";
import { CloudProjectType } from "@/types/cloud-project";
import {
  COMMIT_ID_CACHE_KEY,
  GRAPH_CACHE_KEY,
  ID_CACHE_KEY,
  NAME_CACHE_KEY,
  PREVIEW_CACHE_KEY,
  TRANSFORM_CACHE_KEY,
  UPDATE_TIME_CACHE_KEY,
  USER_ID_CACHE_KEY,
} from "./constants";
import { BoardStore, langStore, useIsReady } from "@noli/business";
import { isString } from "lodash";
import { ValueOf } from "@/types/base";
import { cloudStorage } from "./storage";

export function getNewProjectId(): string {
  return uuidv4();
}

export function getDefaultProjectName(updateTime?: string): string {
  return langStore.tgt === "zh" ? "默认项目" : "Default Work";
  // updateTime ??= new Date().getTime().toString();
  // const name = langStore.tgt === "zh" ? "项目" : "Work";
  // return `${name} ${formatTime(parseInt(updateTime), "YYYY-MM-DD HH:mm").slice(2)}`;
}

export function getLocalUserId(): number | undefined {
  const userId = cloudStorage.get(USER_ID_CACHE_KEY);
  if (isString(userId)) return parseInt(userId);
}

export function updateLocalUserId(localCache: CacheType, userId: number): void {
  if (isUndefined(localCache.userId)) {
    localCache.userId = userId;
    cloudStorage.save(USER_ID_CACHE_KEY, userId.toString());
  }
}

export function saveLocalCacheFrom(data: Partial<CacheType>): void {
  // window.localStorage.setItem(ID_CACHE_KEY, id);
  // window.localStorage.setItem(NAME_CACHE_KEY, name);
  // window.localStorage.setItem(GRAPH_CACHE_KEY, graph.toJson());
  // window.localStorage.setItem(TRANSFORM_CACHE_KEY, JSON.stringify(globalTransform));
  // window.localStorage.setItem(UPDATE_TIME_CACHE_KEY, updateTime.toString());
  // if (isUndefined(userId)) {
  //   window.localStorage.removeItem(USER_ID_CACHE_KEY);
  // } else {
  //   window.localStorage.setItem(USER_ID_CACHE_KEY, userId.toString());
  // }

  const keyMap: Record<keyof CacheType, string> = {
    id: ID_CACHE_KEY,
    name: NAME_CACHE_KEY,
    graph: GRAPH_CACHE_KEY,
    globalTransform: TRANSFORM_CACHE_KEY,
    updateTime: UPDATE_TIME_CACHE_KEY,
    userId: USER_ID_CACHE_KEY,
    commit_id: COMMIT_ID_CACHE_KEY,
    preview: PREVIEW_CACHE_KEY,
  };

  const valueFormatterMap: Record<keyof CacheType, (v: any) => string | void> = {
    id: (id) => id ?? getNewProjectId().toString(),
    name: (name) => name ?? getDefaultProjectName(),
    graph: (graph: Graph) => graph.toJson(),
    globalTransform: (globalTransform: Matrix2DFields) => JSON.stringify(globalTransform),
    updateTime: (updateTime: number) => updateTime.toString(),
    userId: (userId: number | undefined) => {
      if (isUndefined(userId)) {
        cloudStorage.remove(USER_ID_CACHE_KEY);
      } else {
        cloudStorage.save(USER_ID_CACHE_KEY, userId.toString());
      }
    },
    commit_id: (commitId?: string) => {
      if (isUndefined(commitId) || !commitId) {
        cloudStorage.remove(COMMIT_ID_CACHE_KEY);
      } else {
        cloudStorage.save(COMMIT_ID_CACHE_KEY, commitId);
      }
    },
    preview: (preview = "") => {
      cloudStorage.save(PREVIEW_CACHE_KEY, preview || "");
    },
  };

  (Object.keys(data) as (keyof CacheType)[]).forEach((key) => {
    const value = data[key] as ValueOf<CacheType>;
    if (value) {
      if (valueFormatterMap[key]) {
        const formatted = valueFormatterMap[key](value);
        if (typeof formatted === "string") {
          cloudStorage.save(keyMap[key], formatted);
        }
      }
    }
  });
}

export function saveLocalCache(): void {
  const id = cloudStorage.get(ID_CACHE_KEY);
  const name = cloudStorage.get(NAME_CACHE_KEY);
  const isReady = useIsReady();
  if (isReady && id && name) {
    Logger.debug("Saving Cache");
    saveLocalCacheFrom({
      id,
      name,
      graph: BoardStore.graph,
      globalTransform: BoardStore.board.globalTransform,
      updateTime: new Date().getTime(),
      userId: getLocalUserId(),
      preview: cloudStorage.get(PREVIEW_CACHE_KEY),
    });
  }
}

export function getLocalCache(): CacheType | undefined {
  let id = cloudStorage.get(ID_CACHE_KEY);
  if (!id) {
    id = getNewProjectId();
    cloudStorage.save(ID_CACHE_KEY, id);
  }
  let updateTime = cloudStorage.get(UPDATE_TIME_CACHE_KEY);
  if (!updateTime) {
    updateTime = Date.now().toString();
    cloudStorage.save(UPDATE_TIME_CACHE_KEY, updateTime);
  }
  let name = cloudStorage.get(NAME_CACHE_KEY);
  if (!name) {
    name = getDefaultProjectName(updateTime);
    cloudStorage.save(NAME_CACHE_KEY, name);
  }
  const graphJsonString = cloudStorage.get(GRAPH_CACHE_KEY);
  if (!graphJsonString) return;
  const transformJsonString = cloudStorage.get(TRANSFORM_CACHE_KEY);
  if (!transformJsonString) return;
  return {
    id,
    name,
    graph: Graph.fromJson(graphJsonString).clean().graph,
    globalTransform: Matrix2D.fromJson(transformJsonString),
    updateTime: parseInt(updateTime),
    userId: getLocalUserId(),
    commit_id: cloudStorage.get(COMMIT_ID_CACHE_KEY) ?? undefined,
  };
}

export const genDefaultLocalCache = () => {
  const defaultCache: CacheType = {
    id: getNewProjectId(),
    name: getDefaultProjectName(),
    graph: new Graph(),
    globalTransform: Object.assign({}, identityMatrix2DFields),
    userId: loginStore.userState.userInfo.userId ?? undefined,
    updateTime: new Date().getTime(),
  };

  return defaultCache;
};

export function transformLocalToCloudJson(localCache: CacheType): CloudCacheType {
  return {
    graphInfo: shallowCopy(localCache.graph.toJsonInfo()),
    globalTransform: shallowCopy(localCache.globalTransform),
  };
}

export async function loadJson(source: string) {
  const isUrl = /^http?s/i.test(source);
  if (isUrl) {
    return fetch(source).then((res) => res.json());
  } else {
    return JSON.parse(source);
  }
}

export async function transformProjectToLocal(project: CloudProjectType): Promise<CacheType> {
  const cloudCacheJson = project.url || project.content;
  Logger.debug(`>> detected : ${project.project_name}`);
  const cloudCache = await loadJson(cloudCacheJson);
  if (!cloudCache.globalTransform) throw Error("no globalTransform");
  // 云上的更新时间是按秒记的，所以乘上 1000 换算成毫秒
  const cloudUpdateTime = project.update_time * 1000;
  return {
    id: project.project_id,
    name: project.project_name,
    graph: Graph.fromJsonInfo(cloudCache.graphInfo),
    globalTransform: cloudCache.globalTransform,
    updateTime: cloudUpdateTime,
    userId: loginStore.userState.userInfo.userId,
    commit_id: project.commit_id,
    preview: project.preview,
  };
}

export async function syncCloudProjectToLocalCache(
  project: CloudProjectType,
  failed: (e: any, project: CloudProjectType) => Promise<CacheType | undefined>,
): Promise<CacheType | undefined> {
  let localized;
  try {
    localized = await transformProjectToLocal(project);
  } catch (e) {
    return failed(e, project);
  }
  saveLocalCacheFrom(localized);
  return localized;
}

export function getLocalCommitId() {
  return cloudStorage.get(COMMIT_ID_CACHE_KEY);
}

export function updateLocalCommitId(projectId: string, commitId: string) {
  // const localCache = getLocalCache();
  // if (localCache) {
  // if (localCache.id === projectId && localCache.commit_id !== commitId) {
  cloudStorage.save(COMMIT_ID_CACHE_KEY, commitId);
  // }
  // }
}
